/*  MUI  */
import { ArrowDownward, ArrowUpward, Clear } from '@mui/icons-material';
import { Alert, Box, Grid, TableCell, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import {
    LifecyclePicker,
    RiskPicker
} from 'application/Shared/Components/Pickers';
import { ManagedTable } from 'application/Shared/Components/Table';
import { TagLinksList } from 'application/Shared/Components/Tag';
import { Currency } from 'application/Shared/Components/Text';
import { FontSizes } from 'application/Shared/Constants';
import { hmsUsageFormatter } from 'application/Shared/Helpers';
import { formatDistanceToNow } from 'application/Shared/Helpers/Dates';
import Core from 'application/Shared/Helpers/Functions';

/* Objects */
import { Application } from 'objects/Application';
import { User, UserEmail, UserFromContext } from 'objects/User';

/* React */
import React, { useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';

let wastedLicenses = 0;

const BusinessOwnerReports = (props) => {
    const { renderingScheduledReport, reportData, reportSetup } = props;

    return (
        <Box width="100%">
            {reportData?.businessOwnerBusinessOwnerApplicationBreakdown && (
                <DiscoveryApplicationBreakdown
                    {...props}
                    renderingScheduledReport={renderingScheduledReport}
                    reportSetup={reportSetup}
                    reportData={
                        reportData.businessOwnerBusinessOwnerApplicationBreakdown
                    }
                />
            )}
        </Box>
    );
};

const low = 180;
const medium = 30 * 60;
const high = 120 * 60;

const monthUsageFormat = (value) => {
    if (value < low) {
        return {
            cellIcon: (
                <Clear
                    sx={{ color: 'usage.icon.waste', fontSize: FontSizes[3] }}
                />
            )
        };
    }
    if (value < medium) {
        return {
            cellIcon: (
                <ArrowDownward
                    sx={{ color: 'usage.icon.low', fontSize: FontSizes[3] }}
                />
            )
        };
    }
    if (value < high) {
        return {
            cellIcon: (
                <ArrowUpward
                    sx={{ color: 'usage.icon.medium', fontSize: FontSizes[3] }}
                />
            )
        };
    }
    return {
        cellIcon: (
            <>
                <ArrowUpward
                    sx={{
                        color: 'usage.icon.high',
                        fontSize: FontSizes[3],
                        marginRight: ' -5px'
                    }}
                />
                <ArrowUpward
                    sx={{ color: 'usage.icon.high', fontSize: FontSizes[3] }}
                />
            </>
        )
    };
};

const quarterUsageFormat = (value) => {
    if (value < low * 3) {
        return { color: 'usage.icon.waste', textAlign: 'center' };
    }
    if (value < medium * 3) {
        return { color: 'usage.icon.low', textAlign: 'center' };
    }
    if (value < high * 3) {
        return { color: 'usage.icon.medium', textAlign: 'center' };
    }
    return { color: 'usage.icon.high', textAlign: 'center' };
};

const monthUsageGroupCalculator = (value) => {
    if (value < low) {
        return 'Waste';
    }
    if (value < medium) {
        return 'Low Usage';
    }
    if (value < high) {
        return 'Medium Usage';
    }
    return 'High Usage';
};

const headers = ({ reportDataMonths, t }) => [
    {
        align: 'left',
        field: 'riubName',
        renderHeader: <strong>{t('Common.name')}</strong>,
        width: '24%'
    },
    {
        align: 'center',
        field: 'overviewEmail',
        renderHeader: <strong>{t('Common.email')}</strong>,
        width: '10%'
    },
    {
        align: 'left',
        field: 'overviewSubCategory',
        renderHeader: (
            <strong>{t('Reports.BusinessOwnerReport.quarterUsage')}</strong>
        ),
        width: '10%'
    },
    {
        align: 'left',
        field: 'overviewRisk',
        renderHeader: <strong>{reportDataMonths[0]}</strong>,
        width: '12%'
    },
    {
        align: 'left',
        field: 'overviewBusinessOwner',
        renderHeader: <strong>{reportDataMonths[1]}</strong>,
        width: '12%'
    },
    {
        align: 'left',
        field: 'overviewStatus',
        renderHeader: <strong>{reportDataMonths[2]}</strong>,
        width: '12%'
    },
    {
        align: 'left',
        field: 'overviewPurpose',
        renderHeader: <strong>{t('Common.last_used')}</strong>,
        width: '10%'
    }
];

const headersUsage = (t) => [
    {
        align: 'left',
        field: 'riubCategory',
        renderHeader: <strong>{t('Common.month')}</strong>,
        width: '20%'
    },
    {
        align: 'right',
        field: 'riubCategory',
        renderHeader: <strong>{t('Common.waste')}</strong>,
        width: '20%'
    },
    {
        align: 'right',
        field: 'riubCategory',
        renderHeader: <strong>{t('Common.lowUsage')}</strong>,
        width: '20%'
    },
    {
        align: 'right',
        field: 'riubCategory',
        renderHeader: <strong>{t('Common.mediumUsage')}</strong>,
        width: '20%'
    },
    {
        align: 'right',
        field: 'riubCategory',
        renderHeader: <strong>{t('Common.highUsage')}</strong>,
        width: '20%'
    }
];

const DiscoveryApplicationBreakdown = (props) => {
    const theme = useTheme();
    const { reportData, t } = props;

    // Calculate bar data and usage keys
    // Usage Keys should be the months
    const usageKeys = ['Waste', 'Low Usage', 'Medium Usage', 'High Usage'];

    // noUsage -> $40
    // lowUsage -> $100

    // Bar data will accumulate the amount of dollars in each category
    // Bar Graph Y axis will be dollars spent
    const barDataApplicationMap = {};
    const tableDataApplicationMap = {};

    reportData.data.forEach((applicationUsageObject) => {
        const { applicationId, costPerUser, usage, users } =
            applicationUsageObject;

        if (costPerUser === -1) {
            return;
        }

        if (!Core.isset(barDataApplicationMap, applicationId)) {
            barDataApplicationMap[applicationId] = [];
            tableDataApplicationMap[applicationId] = [];
        }

        const usageTotalByMonthAndKey = {
            [reportData.months[0]]: {
                'High Usage': 0,
                'Low Usage': 0,
                'Medium Usage': 0,
                Waste: 0
            },
            [reportData.months[1]]: {
                'High Usage': 0,
                'Low Usage': 0,
                'Medium Usage': 0,
                Waste: 0
            },
            [reportData.months[2]]: {
                'High Usage': 0,
                'Low Usage': 0,
                'Medium Usage': 0,
                Waste: 0
            }
        };

        const quarterTotals = {
            'High Usage': 0,
            'Low Usage': 0,
            'Medium Usage': 0,
            Waste: 0
        };

        // Add the additional dollars wasted for each month. This is calculated by subtracting the total number active users from the subscribed users, then multiplied by mrr
        wastedLicenses = users - usage.length;
        const additionalWaste = wastedLicenses * costPerUser;

        usageTotalByMonthAndKey[reportData.months[0]].Waste += additionalWaste;
        usageTotalByMonthAndKey[reportData.months[1]].Waste += additionalWaste;
        usageTotalByMonthAndKey[reportData.months[2]].Waste += additionalWaste;

        usage.forEach((currentUsage) => {
            usageTotalByMonthAndKey[reportData.months[0]][
                monthUsageGroupCalculator(
                    currentUsage[reportData.startingMonth]
                )
            ] += costPerUser;
            usageTotalByMonthAndKey[reportData.months[1]][
                monthUsageGroupCalculator(
                    currentUsage[reportData.startingMonth + 1]
                )
            ] += costPerUser;
            usageTotalByMonthAndKey[reportData.months[2]][
                monthUsageGroupCalculator(
                    currentUsage[reportData.startingMonth + 2]
                )
            ] += costPerUser;
        });

        Object.entries(usageTotalByMonthAndKey).forEach(([key, value]) => {
            barDataApplicationMap[applicationId].push({
                ...value,
                month: key
            });
            tableDataApplicationMap[applicationId].push({
                ...value,
                month: key
            });
        });

        Object.keys(quarterTotals).forEach((usageKey) => {
            quarterTotals[usageKey] +=
                usageTotalByMonthAndKey[reportData.months[0]][usageKey];
            quarterTotals[usageKey] +=
                usageTotalByMonthAndKey[reportData.months[1]][usageKey];
            quarterTotals[usageKey] +=
                usageTotalByMonthAndKey[reportData.months[2]][usageKey];
        });

        tableDataApplicationMap[applicationId].push({
            ...quarterTotals,
            month: 'Quarter'
        });
    });

    const tableHeadersUsage = useMemo(() => headersUsage(t), [t]);
    const tableHeadersUsersBreakdown = useMemo(
        () => headers({ reportDataMonths: reportData.months, t }),
        [reportData.months, t]
    );

    return (
        <>
            {reportData.data.map((applicationUsageObject) => {
                const appId = applicationUsageObject.applicationId;
                const appCostPerUser = applicationUsageObject.costPerUser;
                const appUsage = applicationUsageObject.usage;
                return (
                    <Box width="100%" key={`iub_appUsageContainer_${appId}`}>
                        <Application
                            key={`iub_app_${appId}`}
                            data={reportData.applicationMap[appId].application}
                            nameHasLink
                        >
                            <Box width="100%">
                                <Typography
                                    component="span"
                                    style={{
                                        textTransform: 'uppercase'
                                    }}
                                >
                                    <ApplicationNameLinkCell
                                        nameNoTruncate
                                        withCategory
                                        categoryNoTruncate
                                    />
                                </Typography>

                                <Box mt="8px">
                                    <Grid container>
                                        <Grid item xs={3}>
                                            <Typography
                                                style={{ fontWeight: 600 }}
                                            >
                                                Risk:
                                            </Typography>
                                            <div>
                                                {reportData.applicationMap[
                                                    appId
                                                ].risk ? (
                                                    <RiskPicker
                                                        visualOnly
                                                        value={
                                                            reportData
                                                                .applicationMap[
                                                                appId
                                                            ].risk
                                                        }
                                                    />
                                                ) : (
                                                    <RiskPicker
                                                        unselectedOption="No Risk Assigned"
                                                        visualOnly
                                                        unselectedValue={null}
                                                        value={null}
                                                    />
                                                )}
                                            </div>
                                        </Grid>
                                        <Grid item xs={5}>
                                            <Typography
                                                style={{ fontWeight: 600 }}
                                            >
                                                Tags:
                                            </Typography>
                                            <div>
                                                <TagLinksList
                                                    tagLinks={
                                                        reportData
                                                            .applicationMap[
                                                            appId
                                                        ]?.tagLinks || []
                                                    }
                                                />
                                            </div>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography
                                                style={{ fontWeight: 600 }}
                                            >
                                                Lifecycle Stage:
                                            </Typography>
                                            <div>
                                                <LifecyclePicker
                                                    visualOnly
                                                    value={
                                                        reportData
                                                            .applicationMap[
                                                            appId
                                                        ].status
                                                    }
                                                />
                                            </div>
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Box mt="16px">
                                    <Typography style={{ fontWeight: 600 }}>
                                        Business Purpose:
                                    </Typography>
                                    <Box>
                                        {reportData.applicationMap[appId]
                                            .purpose
                                            ? reportData.applicationMap[appId]
                                                  .purpose
                                            : 'No Purpose Set'}
                                    </Box>
                                </Box>
                                <hr />
                                <Box width="100%" display="flex">
                                    <Box width="25%">
                                        <div
                                            style={{
                                                backgroundColor:
                                                    theme.palette.usage.barChart
                                                        .Waste,
                                                border: '1px solid',
                                                borderColor:
                                                    theme.palette.usage
                                                        .textColor.waste,
                                                borderRadius: '10px',
                                                marginBottom: '4px',
                                                marginTop: '4px',
                                                padding: '4px',
                                                width: '162px'
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <Box
                                                    mr="4px"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                >
                                                    <Clear
                                                        sx={{
                                                            color: 'usage.badgeIcon.waste',
                                                            fontSize:
                                                                FontSizes[3]
                                                        }}
                                                    />
                                                </Box>
                                                <Typography
                                                    color="usage.badgeTextColor.waste"
                                                    fontWeight={600}
                                                >
                                                    Waste
                                                </Typography>
                                            </Box>
                                        </div>
                                    </Box>
                                    <Box width="25%">
                                        <div
                                            style={{
                                                backgroundColor:
                                                    theme.palette.usage
                                                        .barChart['Low Usage'],
                                                border: '1px solid',
                                                borderColor:
                                                    theme.palette.usage
                                                        .textColor.low,
                                                borderRadius: '10px',
                                                marginBottom: '4px',
                                                marginTop: '4px',
                                                padding: '4px',
                                                width: '162px'
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <Box
                                                    mr="4px"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                >
                                                    <ArrowDownward
                                                        sx={{
                                                            color: 'usage.badgeIcon.low',
                                                            fontSize:
                                                                FontSizes[3]
                                                        }}
                                                    />
                                                </Box>
                                                <Typography
                                                    color="usage.badgeTextColor.low"
                                                    fontWeight={600}
                                                >
                                                    Low Usage
                                                </Typography>
                                            </Box>
                                        </div>
                                    </Box>
                                    <Box width="25%">
                                        <div
                                            style={{
                                                backgroundColor:
                                                    theme.palette.usage
                                                        .barChart[
                                                        'Medium Usage'
                                                    ],
                                                border: '1px solid',
                                                borderColor:
                                                    theme.palette.usage
                                                        .textColor.medium,
                                                borderRadius: '10px',
                                                marginBottom: '4px',
                                                marginTop: '4px',
                                                padding: '4px',
                                                width: '162px'
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <Box
                                                    mr="4px"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                >
                                                    <ArrowUpward
                                                        sx={{
                                                            color: 'usage.badgeIcon.medium',
                                                            fontSize:
                                                                FontSizes[3]
                                                        }}
                                                    />
                                                </Box>
                                                <Typography
                                                    color="usage.badgeTextColor.medium"
                                                    fontWeight={600}
                                                >
                                                    Medium Usage
                                                </Typography>
                                            </Box>
                                        </div>
                                    </Box>
                                    <Box width="25%">
                                        <div
                                            style={{
                                                backgroundColor:
                                                    theme.palette.usage
                                                        .barChart['High Usage'],
                                                border: '1px solid',
                                                borderColor:
                                                    theme.palette.usage
                                                        .textColor.high,
                                                borderRadius: '10px',
                                                marginBottom: '4px',
                                                marginTop: '4px',
                                                padding: '4px',
                                                width: '162px'
                                            }}
                                        >
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                            >
                                                <Box
                                                    mr="4px"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                >
                                                    <ArrowUpward
                                                        sx={{
                                                            color: 'usage.badgeIcon.high',
                                                            fontSize:
                                                                FontSizes[3],
                                                            marginRight: '-5px'
                                                        }}
                                                    />
                                                    <ArrowUpward
                                                        sx={{
                                                            color: 'usage.badgeIcon.high',
                                                            fontSize:
                                                                FontSizes[3]
                                                        }}
                                                    />
                                                </Box>
                                                <Typography
                                                    color="usage.badgeTextColor.high"
                                                    fontWeight={600}
                                                >
                                                    High Usage
                                                </Typography>
                                            </Box>
                                        </div>
                                    </Box>
                                </Box>
                                <hr />
                            </Box>
                            <Box width="100%">
                                {/* Only report on contract information if the contract exists for the application */}
                                {appCostPerUser !== -1 && (
                                    <>
                                        <Box mb="16px">
                                            <Typography
                                                style={{ fontWeight: 600 }}
                                            >
                                                Financial Analysis:
                                            </Typography>

                                            <Box>
                                                {wastedLicenses >= 0 ? (
                                                    <Alert
                                                        severity="warning"
                                                        icon={false}
                                                    >
                                                        <strong>
                                                            {wastedLicenses}{' '}
                                                        </strong>
                                                        Wasted Licenses Found!
                                                    </Alert>
                                                ) : (
                                                    <>
                                                        <Box
                                                            as="span"
                                                            style={{
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            {wastedLicenses *
                                                                -1}
                                                        </Box>{' '}
                                                        <Box as="span">
                                                            Additional Users
                                                            Found!
                                                        </Box>
                                                    </>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box
                                            width="100%"
                                            display="flex"
                                            justifyContent="center"
                                            mb="16px"
                                        >
                                            <BarChart
                                                width={700}
                                                height={240}
                                                data={
                                                    barDataApplicationMap[appId]
                                                }
                                                margin={{
                                                    bottom: 5,
                                                    left: 20,
                                                    right: 30,
                                                    top: 20
                                                }}
                                            >
                                                <CartesianGrid
                                                    stroke={
                                                        theme.palette.chart.axis
                                                    }
                                                    strokeDasharray="3 3"
                                                />
                                                <XAxis dataKey="month" />
                                                <YAxis />
                                                <Tooltip
                                                    itemStyle={{
                                                        color: theme?.palette
                                                            .chart.tooltip
                                                    }}
                                                />
                                                {usageKeys.map((row) => (
                                                    <Bar
                                                        isAnimationActive={
                                                            false
                                                        }
                                                        key={`iuo_${row}`}
                                                        dataKey={row}
                                                        stackId="a"
                                                        fill={
                                                            theme.palette.usage
                                                                .barChart[row]
                                                        }
                                                    />
                                                ))}
                                            </BarChart>
                                        </Box>

                                        <ManagedTable
                                            headers={tableHeadersUsage}
                                            renderBody={(row) => (
                                                <>
                                                    <TableCell
                                                        sx={{
                                                            backgroundColor:
                                                                row.month ===
                                                                    'Quarter' &&
                                                                'table.totalRowBackground'
                                                        }}
                                                    >
                                                        {row.month ===
                                                        'Quarter' ? (
                                                            <strong>
                                                                {row.month}
                                                            </strong>
                                                        ) : (
                                                            row.month
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            backgroundColor:
                                                                row.month ===
                                                                    'Quarter' &&
                                                                'table.totalRowBackground'
                                                        }}
                                                    >
                                                        {row.month ===
                                                        'Quarter' ? (
                                                            <strong>
                                                                <Currency
                                                                    currency={
                                                                        row.Waste
                                                                    }
                                                                />
                                                            </strong>
                                                        ) : (
                                                            <Currency
                                                                currency={
                                                                    row.Waste
                                                                }
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            backgroundColor:
                                                                row.month ===
                                                                    'Quarter' &&
                                                                'table.totalRowBackground'
                                                        }}
                                                    >
                                                        {row.month ===
                                                        'Quarter' ? (
                                                            <strong>
                                                                <Currency
                                                                    currency={
                                                                        row[
                                                                            'Low Usage'
                                                                        ]
                                                                    }
                                                                />
                                                            </strong>
                                                        ) : (
                                                            <Currency
                                                                currency={
                                                                    row[
                                                                        'Low Usage'
                                                                    ]
                                                                }
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            backgroundColor:
                                                                row.month ===
                                                                    'Quarter' &&
                                                                'table.totalRowBackground'
                                                        }}
                                                    >
                                                        {row.month ===
                                                        'Quarter' ? (
                                                            <strong>
                                                                <Currency
                                                                    currency={
                                                                        row[
                                                                            'Medium Usage'
                                                                        ]
                                                                    }
                                                                />
                                                            </strong>
                                                        ) : (
                                                            <Currency
                                                                currency={
                                                                    row[
                                                                        'Medium Usage'
                                                                    ]
                                                                }
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            backgroundColor:
                                                                row.month ===
                                                                    'Quarter' &&
                                                                'table.totalRowBackground'
                                                        }}
                                                    >
                                                        {row.month ===
                                                        'Quarter' ? (
                                                            <strong>
                                                                <Currency
                                                                    currency={
                                                                        row[
                                                                            'High Usage'
                                                                        ]
                                                                    }
                                                                />
                                                            </strong>
                                                        ) : (
                                                            <Currency
                                                                currency={
                                                                    row[
                                                                        'High Usage'
                                                                    ]
                                                                }
                                                            />
                                                        )}
                                                    </TableCell>
                                                </>
                                            )}
                                            renderBodyKey={null}
                                            rows={
                                                tableDataApplicationMap[appId]
                                            }
                                            renderFooter={false}
                                            rowsPerPage={-1}
                                            searchEnabled={false}
                                            sortDirection="DESC"
                                            sortFieldType="number"
                                            sortKey="applicationCount"
                                        />

                                        <hr />
                                    </>
                                )}

                                <Box
                                    mb="8px"
                                    mt="8px"
                                    style={{ fontWeight: 600 }}
                                >
                                    {appCostPerUser !== -1 && (
                                        <>
                                            <br />
                                            <br />
                                            <br />
                                            <br />
                                        </>
                                    )}
                                    User Breakdown:
                                </Box>
                                <Box mb="32px">
                                    <ManagedTable
                                        headers={tableHeadersUsersBreakdown}
                                        renderBody={(row) => (
                                            <User
                                                data={
                                                    reportData.userMap[
                                                        row.platformUserId
                                                    ]
                                                }
                                            >
                                                <TableCell>
                                                    <UserFromContext
                                                        includeTitle
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <small>
                                                        <UserEmail iconOnly />
                                                    </small>
                                                </TableCell>
                                                <TableCell
                                                    style={quarterUsageFormat(
                                                        row.quarter
                                                    )}
                                                >
                                                    <strong>
                                                        {hmsUsageFormatter({
                                                            defaultMessage:
                                                                t(
                                                                    'Common.waste'
                                                                ),
                                                            t,
                                                            value: row.quarter
                                                        })}
                                                    </strong>
                                                </TableCell>
                                                <TableCell>
                                                    {
                                                        monthUsageFormat(
                                                            row[
                                                                reportData
                                                                    .startingMonth
                                                            ]
                                                        ).cellIcon
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    {
                                                        monthUsageFormat(
                                                            row[
                                                                reportData.startingMonth +
                                                                    1
                                                            ]
                                                        ).cellIcon
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    {
                                                        monthUsageFormat(
                                                            row[
                                                                reportData.startingMonth +
                                                                    2
                                                            ]
                                                        ).cellIcon
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    {formatDistanceToNow(
                                                        row.lastUsed
                                                    )}
                                                </TableCell>
                                            </User>
                                        )}
                                        renderBodyKey={null}
                                        renderFooter={false}
                                        rows={appUsage}
                                        rowsPerPage={-1}
                                        searchEnabled={false}
                                        sortDirection="DESC"
                                        sortFieldType="number"
                                        sortKey="usage"
                                    />
                                </Box>
                            </Box>
                        </Application>
                    </Box>
                );
            })}
        </>
    );
};

export default withTranslation()(BusinessOwnerReports);
